/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { isEqual } from 'lodash';

import BaseSelectQuestion from 'bundles/onboarding-common/components/modular/BaseSelectQuestion';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingOptions } from 'bundles/onboarding-common/contexts/OnboardingOptionsContext';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useSkillSelectQuestionState from 'bundles/onboarding-common/hooks/useSkillSelectQuestionState';
import useSkillsWantedData from 'bundles/onboarding-common/hooks/useSkillsWantedData';
import type { Skill } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

const SkillSelectQuestion = ({ skills, skillsWantedData }: { skills: Skill[]; skillsWantedData: Skill[] | null }) => {
  const { skillsPerRow, containerRef, handleClick, onViewMoreClick, onViewAllClick, isLoading, isExpanded } =
    useSkillSelectQuestionState({ skills, skillsWantedData });

  return (
    <BaseSelectQuestion
      itemsPerRow={skillsPerRow}
      containerRef={containerRef}
      handleClick={handleClick}
      onViewMoreClick={onViewMoreClick}
      onViewAllClick={onViewAllClick}
      isLoading={isLoading}
      isExpanded={isExpanded}
      getLabel={(item) => item.skillName || ''}
      ariaLabel={_t('Select your skill(s)')}
    />
  );
};

const SkillSelectQuestionWrapper = () => {
  const { setQuestionValue } = useOnboardingQuestionData();
  const { allSkills, isLoadingSkills } = useOnboardingOptions();
  const { value: skillsWantedData, loading } = useFetchData({
    useQueryData: useSkillsWantedData,
    questionIdForLocalStorage: QUESTION_IDS.skillSelect,
  });

  const previousSkillsWantedData = useRef<Skill[] | null>(null);

  useEffect(() => {
    if (skillsWantedData && !isEqual(skillsWantedData, previousSkillsWantedData.current)) {
      previousSkillsWantedData.current = skillsWantedData;
      setQuestionValue({
        __typename: QUESTION_IDS.skillSelect,
        value: skillsWantedData,
      });
    }
  }, [skillsWantedData, setQuestionValue]);

  if (isLoadingSkills || loading) {
    // TODO: Add a loading state
    return null;
  }

  return <SkillSelectQuestion skills={allSkills} skillsWantedData={skillsWantedData} />;
};

export default SkillSelectQuestionWrapper;
